import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { currentLocale } from '../../i18n';
import { API_URL } from '../../utils/api';
import './AdminElo.css';

/**
 * Admin panel: ELO season and ranking management.
 */
export default function AdminElo({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [season, setSeason] = useState(null);
  const [seasonLoading, setSeasonLoading] = useState(true);

  // Process state
  const [processLoading, setProcessLoading] = useState(false);
  const [processResult, setProcessResult] = useState(null);

  // Recalculate state
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);
  const [showRecalcConfirm, setShowRecalcConfirm] = useState(false);

  // New season state
  const [newSeasonName, setNewSeasonName] = useState('');
  const [noReset, setNoReset] = useState(false);
  const [newSeasonLoading, setNewSeasonLoading] = useState(false);
  const [newSeasonResult, setNewSeasonResult] = useState(null);
  const [showSeasonConfirm, setShowSeasonConfirm] = useState(false);

  // Error state
  const [error, setError] = useState('');

  const fetchSeason = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/elo/season`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        setSeason(await r.json());
      }
    } catch {
      // silent
    } finally {
      setSeasonLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchSeason();
  }, [fetchSeason]);

  const handleProcess = async () => {
    setProcessLoading(true);
    setProcessResult(null);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/elo/process`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.error || data.detail || t('admin.processError'));
      } else {
        setProcessResult(data);
        fetchSeason();
      }
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setProcessLoading(false);
    }
  };

  const handleRecalculate = async () => {
    setShowRecalcConfirm(false);
    setRecalcLoading(true);
    setRecalcResult(null);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/elo/recalculate`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.error || data.detail || t('admin.recalcError'));
      } else {
        setRecalcResult(data);
        fetchSeason();
      }
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setRecalcLoading(false);
    }
  };

  const handleNewSeason = async () => {
    setShowSeasonConfirm(false);
    setNewSeasonLoading(true);
    setNewSeasonResult(null);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/elo/new-season`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newSeasonName, no_reset: noReset }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.error || data.detail || t('admin.seasonCreateError'));
      } else {
        setNewSeasonResult(data);
        setNewSeasonName('');
        setNoReset(false);
        fetchSeason();
      }
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setNewSeasonLoading(false);
    }
  };

  return (
    <div>
      <h1 className="admin-section-title">{t('admin.eloTitle')}</h1>

      {error && <div className="admin-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* Season info */}
      <div className="admin-card">
        <div className="admin-card-title">{t('admin.currentSeason')}</div>
        {seasonLoading ? (
          <div className="admin-info">{t('live.loading')}</div>
        ) : season ? (
          <div className="admin-stat-grid">
            <div className="admin-stat-card">
              <div className="admin-stat-label">{t('admin.nameLabel')}</div>
              <div className="admin-stat-value">{season.name || t('admin.standard')}</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-label">{t('admin.startDate')}</div>
              <div className="admin-stat-value">
                {season.start_date ? new Date(season.start_date).toLocaleDateString(currentLocale()) : '-'}
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-label">{t('nav.players')}</div>
              <div className="admin-stat-value">{season.total_players ?? '-'}</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-label">{t('admin.ranked')}</div>
              <div className="admin-stat-value">{season.ranked_players ?? '-'}</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-label">{t('admin.navMatches')}</div>
              <div className="admin-stat-value">{season.total_matches ?? '-'}</div>
            </div>
          </div>
        ) : (
          <div className="admin-info">{t('admin.loadSeasonFailed')}</div>
        )}
      </div>

      {/* Process unprocessed */}
      {perms.hasAnyCommand('rank.process') && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.processUnprocessed')}</div>
          <div className="admin-card-desc">
            {t('admin.processDesc')}
          </div>
          <div className="admin-row">
            <button
              className="admin-btn"
              onClick={handleProcess}
              disabled={processLoading}
            >
              {processLoading ? t('admin.processingElo') : t('admin.process')}
            </button>
            {processResult && (
              <span className="admin-success">
                {t('admin.processResult', { processed: processResult.processed ?? 0, skipped: processResult.skipped ?? 0 })}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Recalculate */}
      {perms.hasCommand('admin.recalculate') && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.recalcTitle')}</div>
          <div className="admin-card-desc">
            {t('admin.recalcDesc')}
          </div>
          <div className="admin-row">
            <button
              className="admin-btn admin-btn-danger"
              onClick={() => setShowRecalcConfirm(true)}
              disabled={recalcLoading}
            >
              {recalcLoading ? t('admin.recalculating') : t('admin.recalcAll')}
            </button>
            {recalcResult && (
              <span className="admin-success">{t('admin.recalcDone')}</span>
            )}
          </div>
        </div>
      )}

      {/* New season */}
      {perms.hasCommand('admin.new-season') && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.newSeason')}</div>
          <div className="admin-card-desc">
            {t('admin.newSeasonDesc')}
          </div>
          <div className="admin-row">
            <input
              className="admin-input"
              placeholder={t('admin.seasonNamePlaceholder')}
              value={newSeasonName}
              onChange={(e) => setNewSeasonName(e.target.value)}
              style={{ maxWidth: 250 }}
            />
          </div>
          <div className="admin-row" style={{ justifyContent: 'space-between' }}>
            <div>
              <span className="admin-label">{t('admin.noEloTransfer')}</span>
              <span className="admin-info" style={{ display: 'block', fontSize: '0.75rem' }}>
                {t('admin.noEloTransferDesc')}
              </span>
            </div>
            <label className="admin-toggle">
              <input
                type="checkbox"
                checked={noReset}
                onChange={(e) => setNoReset(e.target.checked)}
              />
              <span className="admin-toggle-slider"></span>
            </label>
          </div>
          <div className="admin-row" style={{ marginTop: '0.75rem' }}>
            <button
              className="admin-btn"
              onClick={() => setShowSeasonConfirm(true)}
              disabled={newSeasonLoading || !newSeasonName.trim()}
            >
              {newSeasonLoading ? t('admin.creating') : t('admin.startNewSeason')}
            </button>
            {newSeasonResult && (
              <span className="admin-success">
                {t('admin.seasonCreated', { name: newSeasonResult.name || newSeasonName })}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Recalculate confirm dialog */}
      {showRecalcConfirm && (
        <div className="admin-confirm-overlay" onClick={() => setShowRecalcConfirm(false)}>
          <div className="admin-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>{t('admin.recalcConfirmTitle')}</h3>
            <p>
              {t('admin.recalcConfirmText')}
            </p>
            <div className="admin-confirm-actions">
              <button className="admin-btn admin-btn-secondary" onClick={() => setShowRecalcConfirm(false)}>
                {t('matchDetail.cancel')}
              </button>
              <button className="admin-btn admin-btn-danger" onClick={handleRecalculate}>
                {t('admin.yesRecalc')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New season confirm dialog */}
      {showSeasonConfirm && (
        <div className="admin-confirm-overlay" onClick={() => setShowSeasonConfirm(false)}>
          <div className="admin-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>{t('admin.startSeasonConfirmTitle')}</h3>
            <p>
              {t('admin.seasonColon')} <strong>{newSeasonName}</strong><br />
              {noReset
                ? t('admin.allFrom1000')
                : t('admin.softResetText')}
            </p>
            <div className="admin-confirm-actions">
              <button className="admin-btn admin-btn-secondary" onClick={() => setShowSeasonConfirm(false)}>
                {t('matchDetail.cancel')}
              </button>
              <button className="admin-btn" onClick={handleNewSeason}>
                {t('admin.yesStart')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
